import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser, clearError } from 'shared/stores/authSlice';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../routeConstants';
import { API_CONFIG } from 'shared/config/api';

export default function RegisterPage() {
    const { t } = useTranslation('common');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        acceptTerms: false
    });
    const [errors, setErrors] = useState({});
    const [apiErrors, setApiErrors] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, error, isAuthenticated } = useSelector((state) => state.auth);

    useEffect(() => {
        if (isAuthenticated) navigate(ROUTES.DASHBOARD);
        return () => dispatch(clearError());
    }, [isAuthenticated, navigate, dispatch]);

    // Parse API errors when they come in
    useEffect(() => {
        if (error) {
            try {
                // Try to parse the error as JSON
                const errorObj = JSON.parse(error);
                if (errorObj.errors) {
                    setApiErrors(errorObj.errors);
                } else {
                    setApiErrors({ general: [error] });
                }
            } catch (e) {
                // If parsing fails, treat it as a general error
                setApiErrors({ general: [error] });
            }
        } else {
            setApiErrors(null);
        }
    }, [error]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name || formData.name.length < 2) {
            newErrors.name = t('auth.validation.name');
        }
        if (!formData.phone || formData.phone.length < 8) {
            newErrors.phone = t('auth.validation.phone');
        }
        if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            newErrors.email = t('auth.validation.email');
        }
        if (formData.password.length < 6) {
            newErrors.password = t('auth.validation.password');
        }
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = t('auth.validation.passwordMatch');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        // Clear API errors when user starts typing
        if (apiErrors) {
            setApiErrors(null);
            dispatch(clearError());
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            dispatch(registerUser(formData));
        }
    };

    const handleGoogleLogin = async () => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/auth/google/redirect`);

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            // Get response as text first
            const text = await response.text();

            // Parse response as JSON
            const jsonData = JSON.parse(text);

            // Extract and use redirect_url
            if (jsonData.redirect_url) {
                window.location.href = jsonData.redirect_url;
            } else {
                console.error(t('common.console.noRedirectUrl', 'No redirect URL found in response'));
            }
        } catch (error) {
            console.error(t('common.console.googleLoginError', { error }), error);
        }
    };

    // Helper function to render API error messages
    const renderApiErrors = () => {
        if (!apiErrors || !Object.keys(apiErrors).length) return null;

        return (
            <div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700">
                <div className="font-medium">{t('auth.common.fixErrors', 'Please fix the following errors:')}</div>
                <ul className="mt-1 list-disc pl-5 space-y-1">
                    {Object.entries(apiErrors).map(([field, message]) => (
                        <li key={field}>
                            {field !== 'general'
                                ? t('auth.validation.errorFormat', { field, message })
                                : message}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    return (
        <div className="flex min-h-screen">
            <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24 overflow-y-auto">
                <div className="mx-auto w-full max-w-sm lg:w-96">
                    <div>
                        <h2 className="mt-8 text-2xl font-bold tracking-tight text-gray-900">
                            {t('auth.registerPage.title')}
                        </h2>
                        <p className="mt-2 text-sm text-gray-500">
                            {t('auth.registerPage.alreadyMember')}{' '}
                            <Link to={ROUTES.LOGIN} className="font-semibold text-sky-600 hover:text-sky-500">
                                {t('auth.registerPage.loginHere')}
                            </Link>
                        </p>
                    </div>

                    <div className="mt-10">
                        {/* Google Login Button */}
                        <button
                            onClick={handleGoogleLogin}
                            type="button"
                            className="flex w-full justify-center items-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                <path d="M1 1h22v22H1z" fill="none" />
                            </svg>
                            {t('auth.common.continueWithGoogle', 'Continue with Google')}
                        </button>

                        {/* Divider */}
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="bg-gray-50 px-2 text-gray-500">
                                    {t('auth.common.orContinueWith', 'Or continue with email')}
                                </span>
                            </div>
                        </div>

                        {renderApiErrors()}

                        {/* Error display */}
                        {Object.keys(errors).length > 0 && (
                            <div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700">
                                <div className="font-medium">{t('auth.common.fixErrors', 'Please fix the following errors:')}</div>
                                <ul className="mt-1 list-disc pl-5 space-y-1">
                                    {Object.entries(errors).map(([field, message]) => (
                                        <li key={field}>
                                            {field !== 'general'
                                                ? t('auth.validation.errorFormat', { field, message })
                                                : message}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate className="space-y-6">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-900">
                                    {t('auth.registerPage.fields.name')}
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        autoComplete="name"
                                        required
                                        className={`block w-full rounded-md bg-white px-3 py-2 text-gray-900 outline outline-1 -outline-offset-1 ${errors.name ? 'outline-red-300' : 'outline-gray-300'
                                            } placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-sky-600 sm:text-sm`}
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                    {errors.name && (
                                        <p className="mt-2 text-sm text-red-600">{errors.name}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                                    {t('auth.registerPage.fields.email')}
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        className={`block w-full rounded-md bg-white px-3 py-2 text-gray-900 outline outline-1 -outline-offset-1 ${errors.email ? 'outline-red-300' : 'outline-gray-300'
                                            } placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-sky-600 sm:text-sm`}
                                        value={formData.email}
                                        onChange={handleChange}
                                    />
                                    {errors.email && (
                                        <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-900">
                                    {t('auth.registerPage.fields.phone')}
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="phone"
                                        name="phone"
                                        type="text"
                                        autoComplete="phone"
                                        required
                                        className={`block w-full rounded-md bg-white px-3 py-2 text-gray-900 outline outline-1 -outline-offset-1 ${errors.phone ? 'outline-red-300' : 'outline-gray-300'
                                            } placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-sky-600 sm:text-sm`}
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                    {errors.phone && (
                                        <p className="mt-2 text-sm text-red-600">{errors.phone}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                                    {t('auth.registerPage.fields.password')}
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="new-password"
                                        required
                                        className={`block w-full rounded-md bg-white px-3 py-2 text-gray-900 outline outline-1 -outline-offset-1 ${errors.password ? 'outline-red-300' : 'outline-gray-300'
                                            } placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-sky-600 sm:text-sm`}
                                        value={formData.password}
                                        onChange={handleChange}
                                    />
                                    {errors.password && (
                                        <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="confirmPassword"
                                    className="block text-sm font-medium text-gray-900">
                                    {t('auth.registerPage.fields.confirmPassword')}
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        autoComplete="new-password"
                                        required
                                        className={`block w-full rounded-md bg-white px-3 py-2 text-gray-900 outline outline-1 -outline-offset-1 ${errors.confirmPassword ? 'outline-red-300' : 'outline-gray-300'
                                            } placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-sky-600 sm:text-sm`}
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                    />
                                    {errors.confirmPassword && (
                                        <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex w-full justify-center rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:opacity-50"
                                >
                                    {loading ? t('auth.registerPage.creatingAccount') : t('auth.registerPage.createAccount')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <div className="relative hidden w-0 flex-1 lg:block">
                <img
                    className="absolute inset-0 size-full object-cover"
                    src="https://images.pexels.com/photos/2430953/pexels-photo-2430953.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                    alt={t('auth.registerPage.backgroundAlt')}
                />
            </div>
        </div>
    );
}